import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Add,
  ArrowBack,
  Cancel,
  CheckCircle,
  CancelOutlined,
} from '@mui/icons-material'
import { useForm, useStore } from '@tanstack/react-form'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/manage/hooks/use-notifications'
import { MIN_BUTTON_SIZE } from 'src/v3/manage/constants'
import { ConfirmIcon } from 'src/v3/core/icons'
import { uploadImage } from 'src/v3/utilities/image-upload'
import {
  shouldShowZodFieldError,
  getZodValidationError,
} from 'src/v3/utilities/validation'
import ImageUpload from 'src/v3/components/upload-image'
import { usePageTour } from 'src/v3/tours/hooks/use-page-tour'
import CardSectionHeader from 'src/v3/components/card-section-header'
import {
  fetchCourses,
  fetchLinkedServices,
  fetchSelectedSeries,
  fetchSeries,
  selectCourses,
  selectCoursesStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectSelectedSeries,
  selectSelectedSeriesStatus,
  selectedSeriesReset,
} from '../create.slice'
import { CREATE_SERIES_URLS } from '../constants'
import {
  createSeries,
  getSeriesImageUploadUrl,
  updateSeries,
  updateSeriesEpisodeAssignments,
  updateSeriesPublicationStatus,
} from '../utils/api'
import { SeriesPayload } from './types'
import { seriesFormSchema } from './validation'
import { useSeriesImages } from './hooks/use-series-images'
import SeasonSection, { SeasonState } from './components/season-section'
import AddCoursesModal from './components/modals/add-courses-modal'

let paperDarkInputSx = {
  '& .MuiInputBase-root': { backgroundColor: 'background.paperDark' },
}

function PublishChecklistItem({
  done,
  label,
}: {
  done: boolean
  label: string
}): ReactElement {
  return (
    <Box className="flex items-center gap-2">
      {done ? (
        <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />
      ) : (
        <CancelOutlined fontSize="small" sx={{ color: 'error.main' }} />
      )}
      <Typography
        variant="body2"
        sx={{ color: done ? 'text.primary' : 'text.secondary' }}
      >
        {label}
      </Typography>
    </Box>
  )
}

export default function SeriesForm(): ReactElement {
  let { seriesID } = useParams<{ seriesID: string }>()
  let isEditMode = !!seriesID

  usePageTour('edit-series', isEditMode)

  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { showSuccess, showError } = useNotifications()

  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let seriesDetails = useAppSelector(selectSelectedSeries)
  let seriesStatus = useAppSelector(selectSelectedSeriesStatus)

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)
  useAutoFetch(coursesStatus, fetchCourses)

  useEffect(() => {
    if (!isEditMode || !seriesID) return undefined
    let promise = dispatch(fetchSelectedSeries(seriesID))
    return () => {
      promise.abort()
    }
  }, [isEditMode, seriesID, dispatch])

  useEffect(
    () => () => {
      dispatch(selectedSeriesReset())
    },
    [seriesID, dispatch],
  )

  let isLoading =
    isEditMode &&
    (seriesStatus === 'pending' || seriesStatus === 'uninitialized')

  let {
    banner,
    setBanner,
    thumbnail,
    setThumbnail,
    mobileBanner,
    setMobileBanner,
    existingBanner,
    existingThumbnail,
    existingMobileBanner,
    bannerBasePath,
    thumbnailBasePath,
    mobileBannerBasePath,
  } = useSeriesImages({ seriesDetails, isEditMode, isLoading })

  let [seasons, setSeasons] = useState<SeasonState[]>([])
  let [addCoursesModalSeason, setAddCoursesModalSeason] = useState<
    number | null
  >(null)

  useEffect(() => {
    if (isEditMode && seriesDetails) {
      let seasonNumbers = [
        ...new Set(seriesDetails.courses.map((c) => c.season)),
      ]
      let grouped = seasonNumbers
        .map((seasonNumber) => ({
          seasonNumber,
          courseIDs: seriesDetails.courses
            .filter((c) => c.season === seasonNumber)
            .toSorted((a, b) => a.episode - b.episode)
            .map((c) => c.courseID),
        }))
        .toSorted((a, b) => a.seasonNumber - b.seasonNumber)
      setSeasons(grouped)
    }
  }, [isEditMode, seriesDetails])

  let [isChangingPublishState, setIsChangingPublishState] = useState(false)
  let isPublished = seriesDetails?.published === 'published'
  let currentCourseIDs = seasons.flatMap((s) => s.courseIDs)
  let canPublish =
    currentCourseIDs.length > 0 &&
    currentCourseIDs.every(
      (id) => courses.find((c) => c.id === id)?.published === 'published',
    )

  let handlePublishToggle = useCallback(() => {
    if (!seriesID || isChangingPublishState) return
    let newStatus: 'published' | 'unpublished' = isPublished
      ? 'unpublished'
      : 'published'
    setIsChangingPublishState(true)
    updateSeriesPublicationStatus(seriesID, newStatus)
      .then(() => {
        dispatch(fetchSelectedSeries(seriesID))
        showSuccess(isPublished ? 'Series unpublished' : 'Series published')
      })
      .catch(() =>
        showError(
          isPublished
            ? 'Failed to unpublish series'
            : 'Failed to publish series',
        ),
      )
      .finally(() => setIsChangingPublishState(false))
  }, [
    seriesID,
    isPublished,
    isChangingPublishState,
    dispatch,
    showSuccess,
    showError,
  ])

  let [isAutoSaving, setIsAutoSaving] = useState(false)

  let saveAssignments = useCallback(
    (updatedSeasons: SeasonState[]) => {
      if (!seriesID) return
      let assignments = updatedSeasons.flatMap((s) =>
        s.courseIDs.map((courseID, i) => ({
          courseID,
          season: s.seasonNumber,
          episode: i + 1,
        })),
      )
      setIsAutoSaving(true)
      updateSeriesEpisodeAssignments(seriesID, assignments)
        .catch(() => showError('Failed to save episode assignments'))
        .finally(() => setIsAutoSaving(false))
    },
    [seriesID, showError],
  )

  let handleAddSeason = () =>
    setSeasons((prev) => [
      ...prev,
      {
        seasonNumber:
          prev.length > 0
            ? Math.max(...prev.map((s) => s.seasonNumber)) + 1
            : 1,
        courseIDs: [],
      },
    ])

  let handleDeleteSeason = (seasonNumber: number) =>
    setSeasons((prev) => prev.filter((s) => s.seasonNumber !== seasonNumber))

  let handleAddCourses = (seasonNumber: number, newIDs: string[]) => {
    let updated = seasons.map((s) =>
      s.seasonNumber === seasonNumber
        ? { ...s, courseIDs: [...s.courseIDs, ...newIDs] }
        : s,
    )
    setSeasons(updated)
    saveAssignments(updated)
  }

  let handleRemoveCourse = (seasonNumber: number, courseID: string) => {
    let updated = seasons.map((s) =>
      s.seasonNumber === seasonNumber
        ? { ...s, courseIDs: s.courseIDs.filter((id) => id !== courseID) }
        : s,
    )
    setSeasons(updated)
    saveAssignments(updated)
  }

  let handleReorderCourses = (seasonNumber: number, newCourseIDs: string[]) => {
    let updated = seasons.map((s) =>
      s.seasonNumber === seasonNumber ? { ...s, courseIDs: newCourseIDs } : s,
    )
    setSeasons(updated)
    saveAssignments(updated)
  }

  let defaultValues = useMemo(() => {
    if (isEditMode && seriesDetails && !isLoading) {
      return {
        linkedServiceID: seriesDetails.linkedServiceID ?? '',
        name: seriesDetails.name,
        description: seriesDetails.description,
      }
    }
    return { linkedServiceID: '', name: '', description: '' }
  }, [isEditMode, seriesDetails, isLoading])

  let handleSave = async (values: typeof defaultValues) => {
    try {
      let payload: SeriesPayload = {
        name: values.name,
        description: values.description,
        courses: isEditMode ? seasons.flatMap((s) => s.courseIDs) : [],
      }

      let savedID =
        isEditMode && seriesID
          ? await updateSeries(seriesID, payload).then(() => seriesID)
          : await createSeries(values.linkedServiceID, payload).then(
              (c) => c.id,
            )

      let imageUploads = [
        ...(banner
          ? [
              uploadImage(banner, () =>
                getSeriesImageUploadUrl(savedID, 'banner', banner.type),
              ),
            ]
          : []),
        ...(thumbnail
          ? [
              uploadImage(thumbnail, () =>
                getSeriesImageUploadUrl(savedID, 'thumbnail', thumbnail.type),
              ),
            ]
          : []),
        ...(mobileBanner
          ? [
              uploadImage(mobileBanner, () =>
                getSeriesImageUploadUrl(
                  savedID,
                  'mobileBanner',
                  mobileBanner.type,
                ),
              ),
            ]
          : []),
      ]

      await Promise.all(imageUploads)

      showSuccess(isEditMode ? 'Series updated' : 'Series created')
      if (isEditMode) {
        dispatch(fetchSelectedSeries(savedID))
      } else {
        dispatch(fetchSeries())
        navigate(CREATE_SERIES_URLS.EDIT(savedID))
      }
    } catch {
      showError(
        isEditMode ? 'Failed to update series' : 'Failed to create series',
      )
    }
  }

  let form = useForm({
    defaultValues,
    validators: {
      onMount: seriesFormSchema,
      onChange: seriesFormSchema,
    },
    onSubmit: ({ value }) => handleSave(value),
  })

  let selectedLinkedServiceID = useStore(
    form.store,
    (state) => state.values.linkedServiceID,
  )

  let filteredCourses = useMemo(
    () =>
      selectedLinkedServiceID
        ? courses.filter((c) => c.linkedServiceID === selectedLinkedServiceID)
        : [],
    [courses, selectedLinkedServiceID],
  )

  let availableForModal = useMemo(() => {
    if (addCoursesModalSeason === null) return []
    let alreadyInSeason =
      seasons.find((s) => s.seasonNumber === addCoursesModalSeason)
        ?.courseIDs ?? []
    return filteredCourses.filter((c) => !alreadyInSeason.includes(c.id))
  }, [addCoursesModalSeason, seasons, filteredCourses])

  let handleCancel = () => navigate(CREATE_SERIES_URLS.INDEX)

  if (isLoading) {
    return (
      <Container maxWidth="xl" className="py-6">
        <Box className="flex items-center gap-2 mb-6">
          <IconButton
            onClick={handleCancel}
            aria-label="Go back to Series"
            sx={{ minWidth: MIN_BUTTON_SIZE, minHeight: MIN_BUTTON_SIZE }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h1">
            {isEditMode ? 'Edit Series' : 'Create Series'}
          </Typography>
        </Box>
        <Box className="flex flex-col items-center justify-center gap-3 py-24">
          <CircularProgress />
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Loading series data...
          </Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="flex items-center gap-2 mb-6">
        <IconButton
          onClick={handleCancel}
          aria-label="Go back to Series"
          sx={{ minWidth: MIN_BUTTON_SIZE, minHeight: MIN_BUTTON_SIZE }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" component="h1">
          {isEditMode ? 'Edit Series' : 'Create Series'}
        </Typography>
      </Box>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <Box className="grid grid-cols-1 gap-6 md:grid-cols-12">
          <Box className="col-span-12 md:col-span-8">
            <Card
              className="mb-6"
              sx={{ bgcolor: 'background.paper' }}
              data-tour="series-basic-info-card"
            >
              <CardContent>
                <CardSectionHeader label="Basic Information" className="mb-6" />

                {isEditMode ? (
                  <Box className="mb-4">
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                      className="block mb-1"
                    >
                      Linked Service
                    </Typography>
                    <Typography variant="body1">
                      {linkedServices.find(
                        (ls) => ls.id === seriesDetails?.linkedServiceID,
                      )?.name ??
                        seriesDetails?.linkedServiceID ??
                        '—'}
                    </Typography>
                  </Box>
                ) : (
                  <form.Field name="linkedServiceID">
                    {(field) => (
                      <FormControl
                        fullWidth
                        className="mb-4"
                        sx={paperDarkInputSx}
                        error={shouldShowZodFieldError(field.state.meta)}
                      >
                        <InputLabel id="linked-service-label">
                          Linked Service *
                        </InputLabel>
                        <Select
                          labelId="linked-service-label"
                          label="Linked Service *"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                        >
                          {linkedServices.map((ls) => (
                            <MenuItem key={ls.id} value={ls.id}>
                              {ls.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {shouldShowZodFieldError(field.state.meta) && (
                          <Typography
                            variant="caption"
                            color="error"
                            className="mt-1 ml-3"
                          >
                            {getZodValidationError(field.state.meta.errors)}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  </form.Field>
                )}

                <form.Field name="name">
                  {(field) => (
                    <TextField
                      fullWidth
                      label="Name"
                      required
                      className="mb-4"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      error={shouldShowZodFieldError(field.state.meta)}
                      helperText={
                        shouldShowZodFieldError(field.state.meta)
                          ? getZodValidationError(field.state.meta.errors)
                          : undefined
                      }
                      sx={paperDarkInputSx}
                    />
                  )}
                </form.Field>

                <form.Field name="description">
                  {(field) => (
                    <TextField
                      fullWidth
                      label="Description"
                      required
                      multiline
                      minRows={4}
                      className="mb-4"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      error={shouldShowZodFieldError(field.state.meta)}
                      helperText={
                        shouldShowZodFieldError(field.state.meta)
                          ? getZodValidationError(field.state.meta.errors)
                          : undefined
                      }
                      sx={paperDarkInputSx}
                    />
                  )}
                </form.Field>
              </CardContent>
            </Card>

            {isEditMode && (
              <Card
                className="mb-6"
                sx={{ bgcolor: 'background.paper' }}
                data-tour="series-seasons-card"
              >
                <CardContent>
                  <Box className="flex items-center gap-2 mb-4">
                    <Typography
                      component="h2"
                      className="uppercase"
                      sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        letterSpacing: '.08em',
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Seasons
                    </Typography>
                    <Box className="flex-1 h-px" sx={{ bgcolor: 'divider' }} />
                    <Button
                      size="large"
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={handleAddSeason}
                    >
                      Add Season
                    </Button>
                  </Box>

                  {seasons.length === 0 ? (
                    <Box className="py-8 text-center">
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        No seasons yet. Click &quot;Add Season&quot; to get
                        started.
                      </Typography>
                    </Box>
                  ) : (
                    seasons.map((season) => (
                      <SeasonSection
                        key={season.seasonNumber}
                        season={season}
                        allCourses={filteredCourses}
                        isSaving={isAutoSaving}
                        onAddCourses={() =>
                          setAddCoursesModalSeason(season.seasonNumber)
                        }
                        onRemoveCourse={(id) =>
                          handleRemoveCourse(season.seasonNumber, id)
                        }
                        onReorder={(ids) =>
                          handleReorderCourses(season.seasonNumber, ids)
                        }
                        onDeleteSeason={() =>
                          handleDeleteSeason(season.seasonNumber)
                        }
                      />
                    ))
                  )}
                </CardContent>
              </Card>
            )}
          </Box>

          <Box className="col-span-12 md:col-span-4">
            {isEditMode && (
              <Card
                className="mb-6"
                sx={(theme) => ({
                  bgcolor: 'background.paper',
                  border: `1px solid ${alpha(theme.palette[isPublished ? 'success' : 'warning'].main, 0.3)}`,
                  borderTop: `3px solid ${theme.palette[isPublished ? 'success' : 'warning'].main}`,
                })}
                data-tour="series-publish-card"
              >
                <CardContent>
                  <CardSectionHeader
                    label="Publish Status"
                    component="h3"
                    className="mb-4"
                  />
                  <Box className="flex items-center justify-between mb-4">
                    <Typography variant="body2">Status:</Typography>
                    <Chip
                      label={isPublished ? 'Published' : 'Draft'}
                      color={isPublished ? 'success' : 'warning'}
                      size="medium"
                    />
                  </Box>
                  {!isPublished && (
                    <Box className="flex flex-col gap-2 mb-4">
                      <PublishChecklistItem
                        done={currentCourseIDs.length > 0}
                        label="At least one Season/Episode"
                      />
                      <PublishChecklistItem
                        done={canPublish}
                        label="All courses published"
                      />
                    </Box>
                  )}
                  <Tooltip
                    title={
                      isPublished
                        ? 'Unpublish the series to make it unavailable'
                        : currentCourseIDs.length === 0
                          ? 'Add at least one course before publishing'
                          : !canPublish
                            ? 'All courses in the series must be published'
                            : ''
                    }
                  >
                    <span>
                      <Button
                        fullWidth
                        variant={isPublished ? 'outlined' : 'contained'}
                        color={isPublished ? 'warning' : 'success'}
                        onClick={handlePublishToggle}
                        disabled={
                          isChangingPublishState ||
                          (!isPublished && !canPublish)
                        }
                      >
                        {isChangingPublishState
                          ? 'Saving...'
                          : isPublished
                            ? 'Unpublish'
                            : 'Publish'}
                      </Button>
                    </span>
                  </Tooltip>
                </CardContent>
              </Card>
            )}

            <Card
              className="mb-6"
              sx={{ bgcolor: 'background.paper' }}
              data-tour="series-images-card"
            >
              <CardContent>
                <CardSectionHeader
                  label="Images"
                  component="h3"
                  className="mb-4"
                />

                <Box className="mb-6">
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary' }}
                    className="mb-2"
                  >
                    Banner (4:1)
                  </Typography>
                  <ImageUpload
                    id="series-banner"
                    type="banner"
                    image={banner}
                    setImage={setBanner}
                    existingImage={existingBanner}
                    imageBasePath={bannerBasePath}
                    sxOverride={{
                      backgroundColor: 'background.paperDark',
                      borderColor: 'divider',
                    }}
                  />
                </Box>

                <Box className="mb-6">
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary' }}
                    className="mb-2"
                  >
                    Thumbnail (16:9)
                  </Typography>
                  <ImageUpload
                    id="series-thumbnail"
                    type="thumbnail"
                    image={thumbnail}
                    setImage={setThumbnail}
                    existingImage={existingThumbnail}
                    imageBasePath={thumbnailBasePath}
                    sxOverride={{
                      backgroundColor: 'background.paperDark',
                      borderColor: 'divider',
                    }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary' }}
                    className="mb-2"
                  >
                    Mobile Banner (2:3)
                  </Typography>
                  <ImageUpload
                    id="series-mobile-banner"
                    type="2/3"
                    image={mobileBanner}
                    setImage={setMobileBanner}
                    existingImage={existingMobileBanner}
                    imageBasePath={mobileBannerBasePath}
                    sxOverride={{
                      backgroundColor: 'background.paperDark',
                      borderColor: 'divider',
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Box
          className="sticky bottom-0 z-10 flex justify-end gap-3 py-4 mt-8 border-t"
          sx={{
            bgcolor: 'background.default',
            borderColor: 'divider',
          }}
        >
          <form.Subscribe
            selector={(state) => ({
              isSubmitting: state.isSubmitting,
              linkedServiceID: state.values.linkedServiceID,
              name: state.values.name,
              description: state.values.description,
            })}
          >
            {({ isSubmitting, linkedServiceID, name, description }) => {
              let fieldValues = { linkedServiceID, name, description }
              let parseResult = seriesFormSchema.safeParse(fieldValues)
              let formIsValid = parseResult.success

              let hasDirtyChanges =
                !!banner ||
                !!thumbnail ||
                !!mobileBanner ||
                name !== defaultValues.name ||
                description !== defaultValues.description ||
                (!isEditMode &&
                  linkedServiceID !== defaultValues.linkedServiceID) ||
                false

              let canSave = formIsValid && hasDirtyChanges

              return (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    size="large"
                    startIcon={<Cancel />}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !canSave}
                    size="large"
                    aria-live="polite"
                    aria-busy={isSubmitting}
                    startIcon={<ConfirmIcon />}
                    onClick={() => form.handleSubmit()}
                    data-tour="series-save-button"
                  >
                    {isSubmitting
                      ? 'Saving...'
                      : isEditMode
                        ? 'Update Series'
                        : 'Create Series'}
                  </Button>
                </>
              )
            }}
          </form.Subscribe>
        </Box>
      </form>

      {addCoursesModalSeason !== null && (
        <AddCoursesModal
          isOpen
          seasonNumber={addCoursesModalSeason}
          availableCourses={availableForModal}
          onClose={() => setAddCoursesModalSeason(null)}
          onAdd={(ids) => {
            handleAddCourses(addCoursesModalSeason, ids)
            setAddCoursesModalSeason(null)
          }}
        />
      )}
    </Container>
  )
}
